<template>
  <section class="attendance-root">
    <div class="attendance-toolbar">
      <div class="attendance-date">
        <label for="attendance-date">Data</label>
        <input id="attendance-date" v-model="selectedDate" type="date" />
      </div>

      <div v-if="accessMode === 'edit'" class="attendance-add">
        <button class="attendance-add-btn" type="button" @click="showAddPicker = !showAddPicker">+ Adicionar presença</button>
        <div v-if="showAddPicker" class="attendance-add-panel">
          <select v-model="selectedExtraKey">
            <option value="">Selecione um funcionário</option>
            <option v-for="employee in availableEmployees" :key="employee.key" :value="employee.key">
              {{ employee.name }}{{ employee.username ? ` (@${employee.username})` : '' }}
            </option>
          </select>
          <button class="attendance-action attendance-action--present" type="button" :disabled="!selectedExtraKey" @click="addExtraAttendance">Adicionar</button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="attendance-empty">Carregando presenças...</div>
    <div v-else-if="members.length === 0" class="attendance-empty">Nenhum funcionário disponível nesta data.</div>

    <div v-else class="attendance-grid">
      <article v-for="member in members" :key="member.key" class="attendance-card" :class="member.status === 'present' ? 'is-present' : 'is-absent'">
        <div class="attendance-card-head">
          <div class="attendance-avatar" :style="{ background: avatarColor(member.name) }">
            <img v-if="member.photoUrl" :src="$resolveUrl(member.photoUrl)" alt="foto" />
            <span v-else>{{ initials(member.name) }}</span>
          </div>
          <div class="attendance-person">
            <strong>{{ member.name }}</strong>
            <span v-if="member.username">@{{ member.username }}</span>
            <span v-if="member.roleName" class="attendance-role">{{ member.roleName }}</span>
            <span v-if="!member.inProject" class="attendance-tag">Extra do dia</span>
          </div>
        </div>

        <div class="attendance-status">
          <span class="attendance-pill" :class="member.status === 'present' ? 'attendance-pill--present' : 'attendance-pill--absent'">
            {{ member.status === 'present' ? `Presente${member.time ? ` • ${member.time}` : ''}` : 'Ausente' }}
          </span>
        </div>

        <div v-if="accessMode === 'edit'" class="attendance-actions">
          <button class="attendance-action attendance-action--present" type="button" @click="markPresent(member)">Presente</button>
          <button class="attendance-action attendance-action--absent" type="button" @click="markAbsent(member)">Ausente</button>
        </div>
      </article>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from 'vue'
import { apiFetch } from '@/store/api'

interface AttendanceEmployee {
  personType: 'account' | 'offline'
  personId: string
  key: string
  name: string
  username: string
  roleId: string
  roleName: string
  photoUrl: string
  inProject: boolean
  addedManually: boolean
  status: 'present' | 'absent'
  time: string
}

interface AttendanceResponse {
  date: string
  defaultStartTime: string
  members: AttendanceEmployee[]
  availableEmployees: AttendanceEmployee[]
}

const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626', '#db2777']

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

export default defineComponent({
  name: 'ProjectAttendanceSection',
  props: {
    projectId: { type: String, required: true },
    accessMode: { type: String as () => 'read' | 'edit', default: 'read' }
  },
  setup(props) {
    const loading = ref(false)
    const selectedDate = ref(todayIso())
    const defaultStartTime = ref('07:00')
    const members = ref<AttendanceEmployee[]>([])
    const availableEmployees = ref<AttendanceEmployee[]>([])
    const showAddPicker = ref(false)
    const selectedExtraKey = ref('')

    const initials = (name: string) => {
      const parts = `${name || '?'}`.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return `${name || '?'}`.slice(0, 2).toUpperCase()
    }

    const avatarColor = (name: string) => {
      const code = `${name || '?'}`.charCodeAt(0) || 0
      return AVATAR_COLORS[code % AVATAR_COLORS.length]
    }

    const loadAttendance = async () => {
      if (!props.projectId) return
      loading.value = true
      try {
        const payload = await apiFetch<AttendanceResponse>(`/projects/${encodeURIComponent(props.projectId)}/attendance?date=${selectedDate.value}`)
        members.value = payload.members || []
        availableEmployees.value = payload.availableEmployees || []
        defaultStartTime.value = payload.defaultStartTime || '07:00'
      } finally {
        loading.value = false
      }
    }

    const saveAttendance = async (member: AttendanceEmployee, status: 'present' | 'absent', addedManually = member.addedManually) => {
      let time = ''
      if (status === 'present') {
        const response = window.prompt('Informe o horário de entrada (HH:MM)', member.time || defaultStartTime.value || '07:00')
        if (response === null) return
        time = response.trim()
      }
      await apiFetch(`/projects/${encodeURIComponent(props.projectId)}/attendance`, {
        method: 'POST',
        body: JSON.stringify({
          date: selectedDate.value,
          personType: member.personType,
          personId: member.personId,
          status,
          time,
          addedManually
        })
      })
      await loadAttendance()
    }

    const markPresent = async (member: AttendanceEmployee) => {
      await saveAttendance(member, 'present')
    }

    const markAbsent = async (member: AttendanceEmployee) => {
      await saveAttendance(member, 'absent')
    }

    const addExtraAttendance = async () => {
      const selected = availableEmployees.value.find((employee) => employee.key === selectedExtraKey.value)
      if (!selected) return
      await saveAttendance(selected, 'present', true)
      selectedExtraKey.value = ''
      showAddPicker.value = false
    }

    watch(() => props.projectId, () => {
      selectedDate.value = todayIso()
      loadAttendance().catch(() => undefined)
    })

    watch(selectedDate, () => {
      loadAttendance().catch(() => undefined)
    })

    onMounted(() => {
      loadAttendance().catch(() => undefined)
    })

    return {
      loading,
      selectedDate,
      members,
      availableEmployees,
      showAddPicker,
      selectedExtraKey,
      initials,
      avatarColor,
      markPresent,
      markAbsent,
      addExtraAttendance
    }
  }
})
</script>

<style scoped lang="scss">
.attendance-root {
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.attendance-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.attendance-date,
.attendance-add-panel {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.attendance-date label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-muted, #6b7280);
}

.attendance-add {
  position: relative;
}

.attendance-add-btn,
.attendance-action {
  border: none;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 700;
  cursor: pointer;
}

.attendance-add-btn {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
}

.attendance-add-panel {
  margin-top: 10px;
  padding: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  background: var(--card, #fff);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
}

.attendance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}

.attendance-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 20px;
  padding: 16px;
  background: var(--card, #fff);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.attendance-card.is-present {
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.12);
}

.attendance-card.is-absent {
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.08);
}

.attendance-card-head {
  display: flex;
  gap: 12px;
  align-items: center;
}

.attendance-avatar {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
}

.attendance-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.attendance-person {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.attendance-person strong {
  color: var(--ink, #111827);
}

.attendance-person span {
  color: var(--ink-muted, #6b7280);
  font-size: 13px;
}

.attendance-role,
.attendance-tag {
  display: inline-flex;
  width: fit-content;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
}

.attendance-pill {
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.attendance-pill--present {
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
}

.attendance-pill--absent {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.attendance-actions {
  display: flex;
  gap: 10px;
}

.attendance-action {
  flex: 1;
}

.attendance-action--present {
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
}

.attendance-action--absent {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.attendance-empty {
  padding: 36px 24px;
  text-align: center;
  border: 1px dashed rgba(15, 23, 42, 0.12);
  border-radius: 18px;
  color: var(--ink-muted, #6b7280);
}

@media (max-width: 720px) {
  .attendance-actions {
    flex-direction: column;
  }
}
</style>
